import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const TIERS = [
  { id: "legacy_builder", amount: 25, title: "Legacy Builder", desc: "Sponsor one student's first book and curriculum." },
  { id: "community_partner", amount: 100, title: "Community Partner", desc: "Fund a quarterly mentorship cohort meal + materials." },
  { id: "pathway_sponsor", amount: 500, title: "Pathway Sponsor", desc: "Underwrite a full TCPI track for one community partner." },
  { id: "foundation_champion", amount: 1000, title: "Foundation Champion", desc: "Named annual contributor — recognized in our annual report." },
];

export default function Foundation() {
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(null);
  const [custom, setCustom] = useState("");

  const donate = async (tierId, customAmount) => {
    if (!donor.name || !donor.email) {
      toast.error("Please add your name and email first.");
      return;
    }
    setSubmitting(tierId);
    try {
      const { data } = await api.post("/payments/checkout/donation", {
        tier_id: tierId,
        donor_email: donor.email,
        donor_name: donor.name,
        custom_amount: customAmount || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setSubmitting(null);
    }
  };

  return (
    <div data-testid="foundation-page">
      <Seo
        title="The 1123 Foundation | Sponsor a Student's First Book"
        description="Support the 1123 Foundation: sponsor students' first books, fund mentorship cohorts, and underwrite Crown Path Initiative tracks for community partners."
        path="/foundation"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "NGO",
          name: "The 1123 Foundation",
          url: "https://1123publishing.com/foundation",
          parentOrganization: { "@type": "Organization", name: "1123 Publishing Company" },
          description: "The philanthropic arm of 1123 Publishing Company, funding youth literacy, mentorship, and the Crown Path Initiative.",
        }}
      />
      <section
        className="relative bg-cover bg-center py-28 md:py-36"
        style={{
          backgroundImage:
            "linear-gradient(rgba(14,35,64,0.88), rgba(14,35,64,0.94)), url('https://images.unsplash.com/photo-1472173148041-00294f0814a2?crop=entropy&cs=srgb&fm=jpg&q=85')",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center text-ivory">
          <div className="overline text-gold mb-4">1123 Foundation</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight">Investing In People.<br/>Building Communities.</h1>
          <p className="mt-7 text-ivory/85 max-w-2xl mx-auto leading-relaxed">
            Every dollar funds education, mentorship, youth development, and community transformation
            in places where legacy is the rarest inheritance.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="bg-parchment border border-navy/15 p-6 md:p-8 mb-10" data-testid="donor-info">
            <div className="overline mb-3">Your Information</div>
            <div className="grid md:grid-cols-2 gap-4">
              <input placeholder="Full name" value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} data-testid="donor-name"
                className="bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
              <input type="email" placeholder="Email for receipt" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} data-testid="donor-email"
                className="bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t) => (
              <div key={t.id} className="bg-navy text-ivory p-7 border border-ivory/15 hover:border-gold transition-colors flex flex-col" data-testid={`donation-${t.id}`}>
                <div className="overline text-gold">{t.title}</div>
                <div className="font-serif text-5xl mt-3">${t.amount}</div>
                <p className="text-ivory/75 mt-4 text-sm leading-relaxed flex-1">{t.desc}</p>
                <button onClick={() => donate(t.id)} disabled={submitting === t.id} data-testid={`donate-btn-${t.id}`}
                  className="mt-6 bg-gold text-navy py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white transition-colors disabled:opacity-60">
                  {submitting === t.id ? "Redirecting..." : "Give"}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-parchment border border-navy/15 p-8 md:flex md:items-end md:justify-between gap-6">
            <div>
              <div className="overline">Custom Gift</div>
              <h3 className="font-serif text-2xl text-navy mt-2">Choose your own amount.</h3>
              <p className="text-sm text-charcoal/70 mt-2">Minimum $5. Every gift is tax-deductible to the extent permitted by law.</p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <div className="flex items-center bg-white border border-navy/20">
                <span className="px-3 text-navy/60">$</span>
                <input type="number" min="5" step="1" placeholder="50" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="custom-amount"
                  className="bg-transparent px-2 py-3 focus:outline-none w-32" />
              </div>
              <button onClick={() => donate("custom", parseFloat(custom))} disabled={submitting === "custom" || !custom}
                data-testid="donate-btn-custom"
                className="bg-navy text-ivory px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal disabled:opacity-60">
                {submitting === "custom" ? "..." : "Give"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-parchment border-t border-navy/10">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {[
            { title: "Corporate Sponsorship", body: "Partner with the Foundation on cohort underwriting, branded scholarships, and matching campaigns." },
            { title: "Volunteer", body: "Mentor a cohort, lead a workshop, or join our reading-circle initiative in your local community." },
            { title: "Estate & Planned Giving", body: "Include the 1123 Foundation in your legacy planning. Speak with our development office for guidance." },
          ].map((c) => (
            <div key={c.title} className="bg-white border border-navy/15 p-7">
              <div className="overline">Get Involved</div>
              <div className="font-serif text-2xl text-navy mt-2">{c.title}</div>
              <p className="text-charcoal/80 mt-3 text-sm leading-relaxed">{c.body}</p>
              <a href="mailto:1123publishing@gmail.com" className="inline-block mt-4 text-navy underline underline-offset-4 text-sm">1123publishing@gmail.com</a>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
